/**
 * useTradingWallets Hook
 *
 * 봇 지갑과 카피트레이딩 지갑의 잔액을 실시간으로 조회하는 커스텀 훅
 */

import useSWR from 'swr';
import { getTradingWallets } from '../../api/wallet';
import type { TradingWalletsResponse, WalletBalance } from '../../../types/wallet';
import { useToken } from '../auth/token';
import { getBigNumber } from '../../../utils/getBigNumber';

const SWR_KEY = '/wallet/balances';

export interface TradingWalletsData {
  bot: WalletBalance;
  copy: WalletBalance;
}

export interface UseTradingWalletsReturn {
  data: TradingWalletsData | undefined;
  isLoading: boolean;
  error: any;
  mutate: () => Promise<TradingWalletsResponse | undefined>;
  // 가용 잔액 계산 헬퍼
  getBotAvailable: () => string;
  getCopyAvailable: () => string;
}

/**
 * 봇/카피 지갑 잔액 조회 훅
 *
 * @example
 * const { data, isLoading, mutate, getBotAvailable } = useTradingWallets();
 *
 * // 봇 지갑 가용 잔액
 * const botAvailable = getBotAvailable(); // "1000.50000000"
 *
 * // 카피 지갑 가용 잔액
 * const copyAvailable = getCopyAvailable(); // "500.25000000"
 */
export function useTradingWallets(): UseTradingWalletsReturn {
  const { data: token } = useToken();

  // 토큰이 없으면 요청하지 않음
  const swrKey = token ? SWR_KEY : null;

  const { data, error, mutate, isLoading } = useSWR<TradingWalletsResponse>(
    swrKey,
    async () => {
      const response = await getTradingWallets();
      return response;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      revalidateIfStale: true,
      // 5초마다 자동 갱신 (선택사항, 필요 시 조정)
      // refreshInterval: 5000,
    }
  );

  /**
   * 봇 지갑 가용 잔액 계산 (balance - lockbalance)
   */
  const getBotAvailable = (): string => {
    if (!data?.data?.bot) return '0';
    const { balance, lockbalance } = data.data.bot;
    return getBigNumber(balance).minus(lockbalance).toString();
  };

  /**
   * 카피 지갑 가용 잔액 계산 (balance - lockbalance)
   */
  const getCopyAvailable = (): string => {
    if (!data?.data?.copy) return '0';
    const { balance, lockbalance } = data.data.copy;
    return getBigNumber(balance).minus(lockbalance).toString();
  };

  return {
    data: data?.data,
    isLoading,
    error,
    mutate: () => mutate(),
    getBotAvailable,
    getCopyAvailable,
  };
}
